import NiceModal, { useModal } from '@ebay/nice-modal-react';

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as F from 'components/form/FormLayout.styled';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import NoRecordsFound from 'components/global/NoRecordsFound';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ORDERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

/**
 * @author Burak Ak
 */

const ImageModal = NiceModal.create(({ value, loading }) => {
	const modal = useModal();

	return (
		<Modal maxWidth="small" className="modal-imageInput">
			<Image
				url={value}
				onClick={() => modal.remove()}
				height="100%"
				style={{
					display: loading ? 'none' : 'inline-block',
					objectFit: 'contain',
					paddingTop: '3rem'
				}}
			/>
		</Modal>
	);
});

const OrderItems = NiceModal.create((props) => {
	const { t } = useTranslation();
	const {
		data: { ID, orderID }
	} = props;

	const axiosPrivate = useAxiosPrivate();
	const [products, setProducts] = useState([]);

	const [isLoading, setIsLoading] = useState(false);
	const { showError } = useAlert();

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		try {
			const response = await axiosPrivate.post(ORDERS.GET_ORDER_ITEMS_BY_ID, { ID });

			if (response.status === 200) {
				setProducts(response.data);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (ID) {
			fillForm();
		}
	}, []);

	return (
		<Modal maxWidth="medium" fullwidth className="modal-orderItems">
			<ModalTitle>
				<Typography variant="p1">
					{t('orderItems')} (<strong>{orderID}</strong>)
				</Typography>
			</ModalTitle>
			<F.TabsFormContainer>
				<ModalContent style={{ width: '100%' }}>
					<OrderItemsTable data={products} isLoading={isLoading} />
				</ModalContent>
			</F.TabsFormContainer>
		</Modal>
	);
});

export default OrderItems;

const OrderItemsTable = ({ data, isLoading }) => {
	const columns = React.useMemo(
		() => [
			{
				id: 'product',
				header: 'product',
				accessorKey: 'image',
				renderCell: ({ row }) =>
					row.original.name ? (
						<ProductImage
							data={{ imageURL: row.original.image ?? null, name: row.original.name }}
							sku={row.original.sku}
						/>
					) : (
						<Typography>-</Typography>
					),
				showOnMobile: true
			},
			{
				id: 'quantity',
				header: 'quantity',
				accessorKey: 'quantity',
				size: 50,
				maxSize: 50,
				showOnMobile: true
			},
			{
				id: 'price',
				header: 'price',
				accessorKey: 'price',
				size: 50,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.price} {row.original.currency}
					</Typography>
				)
			},
			{
				id: 'tax',
				header: 'tax',
				accessorKey: 'taxTotal',
				size: 50,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.taxTotal} {row.original.currency}
					</Typography>
				)
			},
			{
				id: 'total',
				header: 'total',
				accessorKey: 'total',
				size: 50,
				maxSize: 50,
				renderCell: ({ row }) => (
					<Typography>
						{row.original.total} {row.original.currency}
					</Typography>
				),
				showOnMobile: true
			},
			{
				id: 'productName',
				header: 'productName',
				accessorKey: 'name',
				showOnExpansion: true,
				renderCell: ({ row }) => <Typography>{row.original.name}</Typography>
			},
			{
				id: 'sku',
				header: 'sku',
				accessorKey: 'SKU',
				showOnExpansion: true,
				hidden: true
			},
			{
				id: 'description',
				header: 'description',
				accessorKey: 'description',
				renderCell: ({ row }) => <Typography>{row.original.description ?? '-'}</Typography>,
				showOnExpansion: true,
				hidden: true
			},
			{
				id: 'productSize',
				header: 'productSize',
				accessorKey: 'size',
				showOnExpansion: true,
				renderCell: ({ row }) => <Typography>{row.original.size ?? '-'}</Typography>,
				hidden: true
			},
			{
				id: 'productPrice',
				header: 'productPrice',
				accessorKey: 'price',
				renderCell: ({ row }) => (
					<Typography>
						{row.original.price} {row.original.currency}
					</Typography>
				),
				showOnExpansion: true
			},
			{
				id: 'productImages',
				header: 'productImages',
				accessorKey: 'imageURLs',
				showOnExpansion: true,
				hidden: true,
				renderCell: ({ row }) => (
					<div style={{ display: 'flex', gap: '2rem', overflow: 'scroll' }}>
						{(row.original.imageURLs ?? []).map((image, index) => (
							<Image
								key={index}
								url={image}
								width="8rem"
								height="8rem"
								style={{ objectFit: 'contain', borderRadius: '4px' }}
								onClick={() => NiceModal.show(ImageModal, { value: image })}
							/>
						))}
					</div>
				)
			}
		],
		[]
	);

	return (
		<Table
			columns={columns}
			data={data}
			loading={isLoading}
			noRecordsFound={
				<NoRecordsFound lottieName="no-items" height="10rem" title="noItems" subTitle="noItemsSubtitle" />
			}
			rowExpansion
			customExpansionGrid={{
				desktop: '1fr 1fr 3fr',
				tablet: '1fr 1fr 2fr',
				mobile: '1fr 1fr'
			}}
		/>
	);
};
